import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';

/**
 * Сервис для генерации PDF бланков игры "Музыкальное Лото" (версия 2)
 * Использует html2canvas и jsPDF напрямую для генерации из React компонентов
 * (аналогично react-to-pdf, но без автоматического сохранения файлов)
 */

/**
 * Создает PDF с бланками, размещая по 2 бланка рядом друг с другом на каждой странице A4 (landscape)
 * @param blankCanvases - Массив canvas элементов с бланками
 * @returns PDF как Blob
 */
const createPDFWithBlanks = async (blankCanvases: HTMLCanvasElement[]): Promise<Blob> => {
  const pdf = new jsPDF({
    unit: 'mm',
    format: 'a4',
    orientation: 'landscape', // Горизонтальная ориентация
    compress: true,
  });

  const pageWidth = 297; // A4 landscape width in mm
  const pageHeight = 210; // A4 landscape height in mm
  const blankWidth = (pageWidth - 20) / 2; // Ширина одного бланка (2 бланка рядом, 5mm отступ слева, 10mm между (для пунктира), 5mm справа)
  const blankHeight = pageHeight - 10; // Высота бланка (5mm отступ сверху и снизу)
  const centerX = pageWidth / 2; // Центр страницы для пунктирной линии

  let xPosition = 5; // Начальная позиция X (отступ слева)

  for (let i = 0; i < blankCanvases.length; i++) {
    const canvas = blankCanvases[i];
    
    // Если это второй бланк на странице, начинаем новую страницу
    if (i > 0 && i % 2 === 0) {
      pdf.addPage();
      xPosition = 5;
    }

    // Конвертируем canvas в изображение
    const imgData = canvas.toDataURL('image/jpeg', 0.98);
    
    // Вычисляем размеры изображения для бланка с сохранением пропорций
    const imgHeight = (canvas.height * blankWidth) / canvas.width;
    const actualHeight = Math.min(imgHeight, blankHeight); // Не превышаем высоту бланка
    const actualWidth = blankWidth; // Используем полную ширину бланка

    // Добавляем изображение бланка на страницу
    pdf.addImage(imgData, 'JPEG', xPosition, 5, actualWidth, actualHeight);

    // Добавляем пунктирную линию между бланками (только если это первый бланк на странице)
    if (i % 2 === 0 && i < blankCanvases.length - 1) {
      // Пунктирная линия по центру страницы
      pdf.setDrawColor(200, 200, 200); // Светло-серый цвет
      pdf.setLineWidth(0.3);
      
      // Рисуем пунктирную линию (чередование коротких отрезков)
      const dashLength = 3; // Длина одного отрезка пунктира
      const gapLength = 2; // Длина промежутка
      let yPos = 5;
      
      while (yPos < pageHeight - 5) {
        pdf.line(centerX, yPos, centerX, Math.min(yPos + dashLength, pageHeight - 5));
        yPos += dashLength + gapLength;
      }
    }

    // Перемещаем позицию для следующего бланка (справа)
    xPosition += actualWidth + 10; // 10mm отступ между бланками (включая место для пунктира)
  }

  // Конвертируем в blob
  const pdfBlob = pdf.output('blob');
  return pdfBlob;
};

/**
 * Скачивает blob как файл
 */
const downloadBlob = (blob: Blob, fileName: string): void => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/**
 * Генерирует canvas для одного бланка (уменьшенного размера для размещения 2 на странице)
 */
const generateBlankCanvas = async (
  blankRef: React.RefObject<HTMLDivElement>
): Promise<HTMLCanvasElement> => {
  if (!blankRef.current) {
    throw new Error('Контейнер бланка не найден');
  }

  // Ждем, чтобы React успел отрендерить компонент
  await new Promise(resolve => {
    void blankRef.current?.offsetHeight; // Принудительный reflow
    setTimeout(resolve, 200);
  });

  // Используем html2canvas для создания изображения бланка
  const canvas = await html2canvas(blankRef.current, {
    scale: 2,
    useCORS: true,
    logging: false,
    allowTaint: false,
    backgroundColor: '#ffffff',
  });

  return canvas;
};

/**
 * Создает PDF файл с несколькими бланками
 * Размещает по 2 бланка на каждой странице A4
 * @param blankRefs - Массив React ref'ов на контейнеры с бланками
 * @param packageName - Название пакета игры (для имени файла)
 */
export const generateBlanksPDFV2 = async (
  blankRefs: React.RefObject<HTMLDivElement>[],
  packageName: string
): Promise<void> => {
  if (blankRefs.length === 0) {
    alert('Ошибка: нет бланков для генерации');
    return;
  }

  try {
    const blankCanvases: HTMLCanvasElement[] = [];

    // Генерируем canvas для каждого бланка
    for (const blankRef of blankRefs) {
      const canvas = await generateBlankCanvas(blankRef);
      blankCanvases.push(canvas);
    }

    // Создаем PDF с размещением по 2 бланка на странице
    const pdfBlob = await createPDFWithBlanks(blankCanvases);

    // Скачиваем файл
    const safePackageName = packageName.replace(/[^a-zA-Zа-яА-Я0-9]/g, '_');
    const fileName = `Музыкальное_Лото_${safePackageName}_${new Date().toISOString().split('T')[0]}.pdf`;
    downloadBlob(pdfBlob, fileName);
  } catch (error) {
    console.error('Ошибка генерации PDF:', error);
    alert('Произошла ошибка при создании PDF. Попробуйте еще раз.');
  }
};

